import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CompanyAvatar from "../../components/CompanyAvatar";
import ConsentBadge from "../../components/ConsentBadge/ConsentBadge";
import DashboardLayout from "../../components/DashboardLayout/DashboardLayout";
import DealOutcomeSummary from "../../components/DealOutcome/DealOutcomeSummary";
import MatchExplanation from "../../components/MatchExplanation/MatchExplanation";
import CompanyProfileModal from "../../components/Modal/CompanyProfileModal";
import { apiUrl, type MatchRequestListItem, type MatchRequestResultItem } from "../../config/api";
import { EMPTY_VALUE } from "../../i18n/format";
import { locationLabel } from "../../lib/regionOptions";
import { canOpenChat, chatBlockedHint } from "../../lib/supplierConsent";
import { hasTrustBadge, trustBadgeLabel, trustBadgeVariant } from "../../lib/trustLabels";
import { useMatchRequestsTotalMatchResultsRetrieve } from "../../services/match-requests/generated/match-requests/match-requests";
import { useConversationsCreateCreate } from "../../services/messages/generated/conversations/conversations";
import "./MatchResultsPage.css";

type WeightingFilter = "all" | "high" | "verified";

const MIN_SCORE_HIGH = 70;

interface FlatMatchItem {
  result: MatchRequestResultItem;
  request: MatchRequestListItem;
}

const MatchResultsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("b2b");
  const { t: tDeals } = useTranslation("deals");
  const [profileSelection, setProfileSelection] = useState<MatchRequestResultItem | null>(null);
  const [search, setSearch] = useState("");
  const [weightingFilter, setWeightingFilter] = useState<WeightingFilter>("all");
  const [showWeightingMenu, setShowWeightingMenu] = useState(false);

  // Fetch total match results using Orval-generated hook
  const {
    data: totalData,
    isLoading: loading,
    error: queryError,
  } = useMatchRequestsTotalMatchResultsRetrieve(search ? { search } : undefined);

  // Create conversation mutation
  const createConversationMutation = useConversationsCreateCreate();

  // Format error for display
  const error = queryError ? t("matchResults.errors.load") : null;

  const allResults: FlatMatchItem[] =
    totalData?.results?.flatMap((d) =>
      (d.results || []).map((result) => ({
        result: result as unknown as MatchRequestResultItem,
        request: d.request as unknown as MatchRequestListItem,
      })),
    ) ?? [];

  const flatResults = allResults.filter(({ result }) => {
    if (weightingFilter === "high") return (result.winWinMatchScore ?? 0) >= MIN_SCORE_HIGH;
    if (weightingFilter === "verified") return hasTrustBadge(result.company);
    return true;
  });

  const summary = totalData?.summary ?? { totalRequests: 0, totalResults: 0 };

  const handleExport = () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    const qs = params.toString();
    const url = apiUrl(`/api/v1/matchRequests/totalMatchResults/export/${qs ? `?${qs}` : ""}`);
    window.location.href = url;
  };

  return (
    <DashboardLayout showSearch={false}>
      <header className="match-results__header">
        <div>
          <h1 className="dashboard__title">{t("matchResults.title")}</h1>
          <p className="dashboard__subtitle">{t("matchResults.subtitle")}</p>
        </div>
        <div className="match-results__header-actions">
          <button
            type="button"
            className="match-results__secondary-button"
            onClick={handleExport}
            disabled={allResults.length === 0}
          >
            <i className="fa-solid fa-download" />
            <span>{t("matchResults.export")}</span>
          </button>
          <div className="match-results__weighting">
            <button
              type="button"
              className="match-results__secondary-button"
              aria-haspopup="true"
              aria-expanded={showWeightingMenu}
              onClick={() => setShowWeightingMenu((v) => !v)}
            >
              <i className="fa-solid fa-sliders" />
              <span>
                {weightingFilter === "all"
                  ? t("matchResults.weighting.button")
                  : t("matchResults.weighting.buttonActive", {
                      filter:
                        weightingFilter === "high"
                          ? t("matchResults.weighting.minScoreShort", { min: MIN_SCORE_HIGH })
                          : t("matchResults.weighting.documentsReviewed"),
                    })}
              </span>
            </button>
            {showWeightingMenu && (
              <ul className="match-results__weighting-menu" role="menu">
                <li>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={weightingFilter === "all"}
                    onClick={() => {
                      setWeightingFilter("all");
                      setShowWeightingMenu(false);
                    }}
                  >
                    {t("matchResults.weighting.all")}
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={weightingFilter === "high"}
                    onClick={() => {
                      setWeightingFilter("high");
                      setShowWeightingMenu(false);
                    }}
                  >
                    {t("matchResults.weighting.high", { min: MIN_SCORE_HIGH })}
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={weightingFilter === "verified"}
                    onClick={() => {
                      setWeightingFilter("verified");
                      setShowWeightingMenu(false);
                    }}
                  >
                    {t("matchResults.weighting.verifiedOnly")}
                  </button>
                </li>
              </ul>
            )}
          </div>
        </div>
      </header>

      {error && (
        <div className="match-results__error" role="alert">
          <i className="fa-solid fa-circle-exclamation" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="match-results__loading">
          <i className="fa-solid fa-spinner fa-spin" />
          <span>{t("matchResults.loading")}</span>
        </div>
      ) : (
        <>
          <div className="match-results__summary">
            <div className="match-results__summary-item">
              <span className="match-results__summary-label">{t("matchResults.summary.totalRequests")}</span>
              <span className="match-results__summary-value">{summary.totalRequests}</span>
            </div>
            <div className="match-results__summary-item">
              <span className="match-results__summary-label">{t("matchResults.summary.totalResults")}</span>
              <span className="match-results__summary-value">{summary.totalResults}</span>
            </div>
          </div>

          <div className="match-results__search-row">
            <div className="match-results__search-input">
              <i className="fa-solid fa-magnifying-glass" />
              <input
                type="search"
                placeholder={t("matchResults.searchPlaceholder")}
                aria-label={t("matchResults.searchAria")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="match-results__list">
            {flatResults.length === 0 ? (
              <p className="match-results__empty">{t("matchResults.empty")}</p>
            ) : (
              flatResults.map(({ result, request }) => {
                const company = result.company;
                const badgeType = trustBadgeVariant(company);
                const badgeLabel = trustBadgeLabel(company);
                const metaParts = [company.companyIndustry, locationLabel(company.country, company.region)].filter(
                  (part): part is string => Boolean(part),
                );
                return (
                  <article
                    key={`${request.id}-${result.id}`}
                    className="match-results__card"
                    aria-label={t("matchResults.cardAria", { name: company.companyName })}
                  >
                    <header className="match-results__card-header">
                      <div className="match-results__company-main">
                        <CompanyAvatar
                          src={company.companyAvatar}
                          name={company.companyName}
                          size="lg"
                          className="match-results__avatar"
                        />
                        <div className="match-results__company-text">
                          <div className="match-results__company-top-row">
                            <h2 className="match-results__company-name">{company.companyName}</h2>
                            <span className={`match-results__trust-badge match-results__trust-badge--${badgeType}`}>
                              {badgeLabel}
                            </span>
                            <ConsentBadge status={result.consentStatus} audience="buyer" />
                            {result.outcome && result.outcome.status !== "open" ? (
                              <DealOutcomeSummary outcome={result.outcome} compact />
                            ) : null}
                          </div>
                          {metaParts.length > 0 && (
                            <div className="match-results__company-meta">
                              <span>{metaParts.join(" · ")}</span>
                            </div>
                          )}
                          {company.referralDisclosure ? (
                            <div className="match-results__company-meta match-results__disclosure">
                              <em>{t("matchResults.disclosure", { text: company.referralDisclosure })}</em>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="match-results__score-block">
                        <span className="match-results__score-label">{t("matchResults.card.score")}</span>
                        <span className="match-results__score-value">{result.winWinMatchScore}%</span>
                        <span className="match-results__score-trend">
                          <i className="fa-solid fa-arrow-trend-up" />
                        </span>
                      </div>
                    </header>

                    <section className="match-results__matched-section">
                      <div className="match-results__matched-pill">{t("matchResults.card.matchedItem")}</div>
                      <div className="match-results__matched-content">
                        <div className="match-results__matched-main">
                          <p className="match-results__matched-name">{result.matchedItem?.name || EMPTY_VALUE}</p>
                          {result.matchedItem?.hsCode && (
                            <p className="match-results__matched-hs">
                              {t("matchResults.card.hsCode", { code: result.matchedItem.hsCode })}
                            </p>
                          )}
                        </div>
                      </div>
                    </section>

                    <section className="match-results__ahp-section">
                      <p className="match-results__ahp-title">{t("matchResults.card.ahpBreakdown")}</p>
                      <div className="match-results__ahp-grid">
                        <div className="match-results__criterion-row">
                          <div className="match-results__criterion-label">{t("matchResults.card.industryMatch")}</div>
                          <div className="match-results__criterion-bar-wrapper">
                            <div
                              className="match-results__criterion-bar"
                              style={{
                                width: `${result.industryMatch}%`,
                              }}
                            />
                          </div>
                          <div className="match-results__criterion-value">{result.industryMatch}%</div>
                        </div>
                        <div className="match-results__criterion-row">
                          <div className="match-results__criterion-label">
                            {t("matchResults.card.geographicLocation")}
                          </div>
                          <div className="match-results__criterion-bar-wrapper">
                            <div
                              className="match-results__criterion-bar"
                              style={{
                                width: `${result.geographicLocation}%`,
                              }}
                            />
                          </div>
                          <div className="match-results__criterion-value">{result.geographicLocation}%</div>
                        </div>
                      </div>
                    </section>

                    <section className="match-results__why-section">
                      <MatchExplanation lines={result.explanation} confidence={result.matchConfidence} compact />
                    </section>

                    <footer className="match-results__card-footer">
                      <button
                        type="button"
                        className="match-results__footer-button match-results__footer-button--secondary"
                        onClick={() => setProfileSelection(result)}
                      >
                        {t("matchResults.card.viewProfile")}
                      </button>
                      {result.consentStatus === "accepted" && (
                        <button
                          type="button"
                          className="match-results__footer-button match-results__footer-button--secondary"
                          onClick={() => navigate(`/b2b/deals/${result.id}`)}
                        >
                          <i className="fa-regular fa-folder-open" />
                          <span>{tDeals("workspace.openDeal")}</span>
                        </button>
                      )}
                      <button
                        type="button"
                        className="match-results__footer-button match-results__footer-button--primary"
                        disabled={!canOpenChat(result.consentStatus)}
                        title={chatBlockedHint(result.consentStatus) ?? undefined}
                        onClick={() => {
                          createConversationMutation.mutate(
                            { data: { matchResultId: result.id } },
                            {
                              onSuccess: (conv) => {
                                navigate("/b2b/messages", {
                                  state: {
                                    openConversationId: conv.id,
                                    isNewConversation: true,
                                  },
                                });
                              },
                              onError: () => {
                                // Shown by the global mutation toast (src/lib/queryClient.ts).
                              },
                            },
                          );
                        }}
                      >
                        <i className="fa-solid fa-paper-plane" />
                        <span>{t("matchResults.card.initiateContact")}</span>
                      </button>
                    </footer>
                  </article>
                );
              })
            )}
          </div>
        </>
      )}

      {profileSelection && (
        <CompanyProfileModal
          isOpen={!!profileSelection}
          onClose={() => setProfileSelection(null)}
          result={profileSelection}
          contactBlockedReason={chatBlockedHint(profileSelection.consentStatus)}
          onInitiateContact={(matchResultId) => {
            createConversationMutation.mutate(
              { data: { matchResultId } },
              {
                onSuccess: (conv) => {
                  setProfileSelection(null);
                  navigate("/b2b/messages", {
                    state: {
                      openConversationId: conv.id,
                      isNewConversation: true,
                    },
                  });
                },
                onError: () => {
                  // Shown by the global mutation toast (src/lib/queryClient.ts).
                },
              },
            );
          }}
        />
      )}
    </DashboardLayout>
  );
};

export default MatchResultsPage;
